<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { API, HOST } from '@/utils/http'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { MessageSquarePlus, ArrowLeft } from 'lucide-vue-next'
import DialogCreateChat from '@/components/dialogs/DialogCreateChat.vue'
import DialogAddLayer from '@/components/dialogs/DialogAddLayer.vue'
import ChatSessionsList from '@/components/chat/ChatSessionsList.vue'
import ChatConversation from '@/components/chat/ChatConversation.vue'
import { useChatWebSocket } from '@/composables/useChatWebSocket'

const route = useRoute()
const router = useRouter()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const sessionCode = computed(() => route.params.session_code)
const selectedRange = computed(() => store.state.layers.selectedRange)

// State
const chatSessions = ref([])
const showCreateDialog = ref(false)
const showAddLayerDialog = ref(false)
const currentMessages = ref([])
const isGenerating = ref(false)
const connectedLayers = ref([])

// WebSocket for real-time streaming
const {
  isConnected: wsConnected,
  isStreaming: wsStreaming,
  streamingMessage,
  streamingReport,
  connectionError,
  connect: connectWebSocket,
  disconnect: disconnectWebSocket,
  onMessageChunk,
  onReportChunk,
  onStreamComplete,
  onError
} = useChatWebSocket()

// View state - based on route
const showChatView = computed(() => !!sessionCode.value)

// Load all chat sessions
const loadChatSessions = async () => {
  if (!orgId.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/chats`
    })

    chatSessions.value = response.data || []
  } catch (error) {
    console.error('Error loading chat sessions:', error)
    toast({
      title: 'Error',
      description: 'Failed to load chat sessions',
      variant: 'destructive'
    })
  }
}

// Select a session - navigate to it
const selectSession = (session) => {
  router.push({
    name: 'ChatSession',
    params: {
      org_id: orgId.value,
      session_code: session.code
    }
  })
}

// Back to list - navigate to OrgReports
const backToList = () => {
  router.push({ name: 'OrgReports', params: { org_id: orgId.value } })
}

// Load session data
const loadSessionData = async () => {
  if (!sessionCode.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}`
    })

    const sessionData = response.data
    currentMessages.value = sessionData.messages || []

    // Load connected layers
    await loadSessionLayers()

    // Connect to WebSocket for real-time updates
    try {
      await connectWebSocket(orgId.value, sessionCode.value)
      console.log('[ReportsList] WebSocket connected successfully')
    } catch (error) {
      console.error('[ReportsList] WebSocket connection failed:', error)
      toast({
        title: 'Connection Warning',
        description: 'Real-time updates may not work. Please refresh if needed.',
        variant: 'destructive'
      })
    }
  } catch (error) {
    console.error('Error loading session:', error)
    toast({
      title: 'Error',
      description: 'Failed to load session details',
      variant: 'destructive'
    })
  }
}

// Load all layers connected to the session
const loadSessionLayers = async () => {
  if (!sessionCode.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}/layers`
    })

    const sessionLayers = response.data || []

    // Load full layer data for each session layer
    const layersWithData = await Promise.all(
      sessionLayers.map(async (sessionLayer) => {
        try {
          // Skip if layer_code is null or undefined
          if (!sessionLayer.layer_code) {
            console.warn('Session layer has no layer_code:', sessionLayer)
            return null
          }

          const layerResponse = await API({
            method: 'GET',
            url: `${HOST}/org/${orgId.value}/layers/${sessionLayer.layer_code}`
          })

          // Only fetch features if date range is selected (match Layer Detail behavior)
          const [startDate, endDate] = selectedRange.value || []
          if (!startDate || !endDate) {
            // No date range selected - don't fetch features (same as Layer Detail)
            return {
              ...layerResponse.data,
              features_count: 0
            }
          }

          // Build features URL with date filtering
          const featuresUrl = `${HOST}/org/${orgId.value}/layers/${sessionLayer.layer_code}/features?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`

          const featuresResponse = await API({
            method: 'GET',
            url: featuresUrl
          })

          return {
            ...layerResponse.data,
            features_count: featuresResponse.data.features?.length || 0
          }
        } catch (error) {
          console.error(`Error loading layer ${sessionLayer.layer_code}:`, error)
          return null
        }
      })
    )

    connectedLayers.value = layersWithData.filter(layer => layer !== null)
  } catch (error) {
    console.error('Error loading session layers:', error)
  }
}

// Send message
const sendMessage = async (content) => {
  if (isGenerating.value || !sessionCode.value) return

  isGenerating.value = true

  const userMessage = {
    id: Date.now(),
    code: `temp-${Date.now()}`,
    role: 'user',
    content: content,
    created_at: new Date().toISOString()
  }

  currentMessages.value.push(userMessage)

  try {
    await API({
      method: 'POST',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}/messages`,
      data: { content }
    })

    // WebSocket will handle real-time updates
    // No need for polling or manual reload
  } catch (error) {
    console.error('Error sending message:', error)
    isGenerating.value = false

    // Remove the temporary user message
    currentMessages.value = currentMessages.value.filter(msg => msg.code !== userMessage.code)

    // Extract error message from response
    let errorMessage = 'Failed to send message'
    if (error.response) {
      if (error.response.status === 429) {
        errorMessage = 'Rate limit exceeded. You have reached the maximum number of messages allowed per day.'
      } else if (error.response.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : error.response.data.detail.message || 'Failed to send message'
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message
      }
    }

    // Add error message to chat as assistant message
    const timestamp = Date.now()
    const errorMsg = {
      id: timestamp,
      code: `error-${timestamp}-${Math.random().toString(36).slice(2, 11)}`,
      role: 'assistant',
      content: `⚠️ ${errorMessage}`,
      created_at: new Date().toISOString()
    }
    currentMessages.value.push(errorMsg)
  }
}

// Reload current session
const reloadSession = async () => {
  if (!sessionCode.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}`
    })
    currentMessages.value = response.data.messages || []
  } catch (error) {
    console.error('Error reloading session:', error)
  }
}

// WebSocket event handlers
// Handle stream completion
onStreamComplete((data) => {
  console.log('[ReportsList] Stream complete:', data)
  isGenerating.value = false

  // Reload session to get the saved message and report
  reloadSession()

  toast({
    title: 'Response complete',
    description: `Generated in ${(data.stats.processing_time_ms / 1000).toFixed(1)}s`,
    variant: 'default'
  })
})

// Handle WebSocket errors
onError((error) => {
  console.error('[ReportsList] WebSocket error:', error)
  isGenerating.value = false
  toast({
    title: 'Connection Error',
    description: 'Lost connection to chat server. Please refresh.',
    variant: 'destructive'
  })
})

// Open create chat dialog
const openCreateDialog = () => {
  showCreateDialog.value = true
}

// Create new chat session (no layer required initially)
const createNewChat = async ({ name }) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId.value}/chats`,
      data: {
        title: name,
        visibility: 'private'
      }
    })

    const newSession = response.data

    // Add to sessions list
    chatSessions.value.unshift(newSession)

    // Close dialog
    showCreateDialog.value = false

    // Navigate to the new session
    selectSession(newSession)

    toast({
      title: 'Chat created',
      description: 'Add layers to start analyzing your data',
      variant: 'default'
    })
  } catch (error) {
    console.error('Error creating chat:', error)
    toast({
      title: 'Error',
      description: 'Failed to create chat session',
      variant: 'destructive'
    })
  }
}

// Add layer to current session
const addLayerToSession = async (layer) => {
  if (!sessionCode.value) return

  try {
    await API({
      method: 'POST',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}/layers`,
      data: {
        layer_code: layer.code
      }
    })

    // Reload layers
    await loadSessionLayers()

    // Close dialog
    showAddLayerDialog.value = false

    toast({
      title: 'Layer added',
      description: `${layer.name} has been connected to this chat`,
      variant: 'default'
    })
  } catch (error) {
    console.error('Error adding layer:', error)
    const errorMsg = error.response?.data?.detail || 'Failed to add layer'
    toast({
      title: 'Error',
      description: errorMsg,
      variant: 'destructive'
    })
  }
}

// Remove layer from current session
const removeLayerFromSession = async (layerCode) => {
  if (!sessionCode.value) return

  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}/layers/${layerCode}`
    })

    // Reload layers
    await loadSessionLayers()

    toast({
      title: 'Layer removed',
      description: 'Layer has been disconnected from this chat',
      variant: 'default'
    })
  } catch (error) {
    console.error('Error removing layer:', error)
    toast({
      title: 'Error',
      description: 'Failed to remove layer',
      variant: 'destructive'
    })
  }
}

// Handle layer click - navigate to layer view
const handleLayerClick = (layer) => {
  router.push({
    name: 'LayerDetail',
    params: {
      org_id: orgId.value,
      layer_id: layer.code
    }
  })
}

// Delete session
const deleteSession = async (session) => {
  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId.value}/chats/${session.code}`
    })

    // Remove from list
    chatSessions.value = chatSessions.value.filter(s => s.code !== session.code)

    toast({
      title: 'Deleted',
      description: 'Chat session deleted',
      variant: 'default'
    })
  } catch (error) {
    console.error('Error deleting session:', error)
    toast({
      title: 'Error',
      description: 'Failed to delete session',
      variant: 'destructive'
    })
  }
}

// Watch for session code changes
watch(sessionCode, (newCode, oldCode) => {
  if (newCode && newCode !== oldCode) {
    loadSessionData()
  } else if (!newCode) {
    // Clear data when navigating back to list
    currentMessages.value = []
    connectedLayers.value = []

    // Disconnect WebSocket
    disconnectWebSocket()
  }
}, { immediate: true })

// Watch for date range changes and reload layers (debounced to avoid excessive API calls)
const dateRangeDebounceTimeout = ref(null)
watch(selectedRange, () => {
  if (sessionCode.value) {
    // Clear existing timeout
    if (dateRangeDebounceTimeout.value) {
      clearTimeout(dateRangeDebounceTimeout.value)
    }

    // Debounce by 500ms
    dateRangeDebounceTimeout.value = setTimeout(() => {
      loadSessionLayers()
    }, 500)
  }
})

// On mount
onMounted(async () => {
  await loadChatSessions()
})

// Cleanup
onUnmounted(() => {
  disconnectWebSocket()

  // Clear debounce timeout
  if (dateRangeDebounceTimeout.value) {
    clearTimeout(dateRangeDebounceTimeout.value)
  }
})
</script>

<template>
  <div class="flex flex-col overflow-hidden min-h-full">
    <!-- LIST VIEW: Show when no session selected -->
    <template v-if="!showChatView">
      <!-- Header with New Button -->
      <div class="px-1 pb-1 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center justify-between">
          <h2 class="text-sm font-semibold">Chats</h2>
          <Button @click="openCreateDialog" size="sm" variant="default" class="gap-1">
            <MessageSquarePlus class="h-4 w-4" />
            New
          </Button>
        </div>
      </div>

      <!-- Sessions List -->
      <div class="flex-1 min-h-0 overflow-hidden">
        <ChatSessionsList
          :sessions="chatSessions"
          :active-session="null"
          @select="selectSession"
          @delete="deleteSession"
        />
      </div>
    </template>

    <!-- CHAT VIEW: Show when session is selected -->
    <template v-else>
      <!-- Back Button Header -->
      <div class="px-1 pb-1 border-b border-gray-200 flex-shrink-0">
        <Button
          @click="backToList"
          variant="ghost"
          size="sm"
          class="gap-2"
        >
          <ArrowLeft class="h-4 w-4" />
          All Chats
        </Button>
      </div>

      <!-- Chat Conversation -->
      <div class="flex-1 min-h-0 overflow-hidden">
        <ChatConversation
          :messages="currentMessages"
          :is-generating="isGenerating"
          :connected-layers="connectedLayers"
          :is-streaming="wsStreaming"
          :streaming-message="streamingMessage"
          :org-id="orgId"
          @send-message="sendMessage"
          @add-layer="() => showAddLayerDialog = true"
          @remove-layer="removeLayerFromSession"
          @layer-click="handleLayerClick"
        />
      </div>
    </template>

    <!-- Create Chat Dialog -->
    <DialogCreateChat
      :org-id="orgId"
      v-model:open="showCreateDialog"
      @create="createNewChat"
    />

    <!-- Add Layer Dialog -->
    <DialogAddLayer
      :org-id="orgId"
      v-model:open="showAddLayerDialog"
      :connected-layers="connectedLayers"
      :max-layers-reached="connectedLayers.length >= 10"
      @add="addLayerToSession"
    />
  </div>
</template>
